import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useClient } from '../../../contexts/ClientContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../utils/permissions';
import {
  FiTag as TicketIcon,
  FiPlus as Plus,
  FiSearch,
  FiClock,
  FiAlertCircle,
  FiCheckCircle,
  FiXCircle,
  FiMessageSquare,
  FiUser,
  FiCalendar,
  FiFilter,
} from 'react-icons/fi';
import { supportAPI } from '../../../services/realApi';
import SupportRequest from '../../../components/SupportRequest';
import TicketDetail, { Ticket } from '../../../components/TicketDetail';

interface StatusOption {
  value: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface PriorityOption {
  value: string;
  label: string;
}

const STATUS_OPTIONS: StatusOption[] = [
  { value: 'all', label: 'All Tickets', icon: TicketIcon },
  { value: 'open', label: 'Open', icon: FiAlertCircle },
  { value: 'in_progress', label: 'In Progress', icon: FiClock },
  { value: 'resolved', label: 'Resolved', icon: FiCheckCircle },
  { value: 'closed', label: 'Closed', icon: FiXCircle },
];

const PRIORITY_OPTIONS: PriorityOption[] = [
  { value: 'all', label: 'All Priorities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

interface SupportTicket {
  id: string;
  subject?: string;
  description?: string;
  status?: string;
  priority?: string;
  request_type?: string;
  requestType?: string;
  supportChannel?: string;
  requester_name?: string;
  requester_email?: string;
  assignee?: string;
  created_at?: string;
  replies?: unknown[];
}

const Support = () => {
  const { user } = useAuth();
  const { selectedClientId, isAWSUser } = useClient();
  const { hasPermission, isAdmin } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const isClientUser = Boolean(user?.client_id);
  const hasPartnerAccess =
    hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
    hasPermission(PERMISSIONS.PARTNERS_MANAGE);
  const [supportRequests, setSupportRequests] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSupportRequest, setShowSupportRequest] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Check if user can manage support (partners, AWS, admins)
  const canManageSupport =
    isAdmin ||
    hasPermission(PERMISSIONS.ADMIN_VIEW) ||
    hasPartnerAccess ||
    isAWSUser;

  // Clients and users with partner permissions can create tickets
  const canCreateTicket = isClientUser || hasPartnerAccess;

  const loadSupportRequests = useCallback(async () => {
    setLoading(true);
    try {
      let result;

      // Route to correct endpoint based on permissions
      if (hasPartnerAccess) {
        // Users with partner permissions use GET /support/requests (sees only their clients' tickets)
        result = await supportAPI.getSupportRequests();
      } else if (isAdmin || hasPermission(PERMISSIONS.ADMIN_VIEW) || isAWSUser) {
        // Admins and AWS users use GET /support/requests/all
        const clientId = isAWSUser ? selectedClientId : null;
        result = await supportAPI.getAllSupportRequests(clientId);
      } else if (isClientUser) {
        // Clients use GET /support/requests/all
        result = await supportAPI.getAllSupportRequests();
      } else {
        result = { success: false, data: [] };
      }

      if (result?.success) {
        setSupportRequests(result.data || []);
      } else {
        console.error('Failed to load support requests:', result?.error);
        setSupportRequests([]);
      }
    } catch (error: unknown) {
      console.error('Error loading support requests:', error);
      setSupportRequests([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClientId, isAWSUser, hasPermission, isAdmin, hasPartnerAccess, isClientUser]);

  useEffect(() => {
    loadSupportRequests();
  }, [loadSupportRequests]);

  // Handle query params for ticket selection
  useEffect(() => {
    const ticketParam = searchParams.get('ticket');

    // Auto-select ticket if specified in URL and support requests are loaded
    if (ticketParam && supportRequests.length > 0 && !loading) {
      const ticket = supportRequests.find((t) => t.id === ticketParam);
      if (ticket && (!selectedTicket || selectedTicket.id !== ticketParam)) {
        setSelectedTicket(ticket);
      }
    }
  }, [searchParams, supportRequests, loading, selectedTicket]);

  // Update URL when ticket is selected
  const handleSelectTicket = useCallback((ticket: SupportTicket | null) => {
    setSelectedTicket(ticket);
    if (ticket) {
      const params = new URLSearchParams(searchParams);
      params.set('ticket', ticket.id);
      if (selectedClientId) {
        params.set('client', selectedClientId);
      }
      setSearchParams(params, { replace: true });
    } else {
      const params = new URLSearchParams(searchParams);
      params.delete('ticket');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams, selectedClientId]);

  // Compute statistics
  const stats = useMemo(() => {
    const open = supportRequests.filter((r) => r.status?.toLowerCase() === 'open').length;
    const inProgress = supportRequests.filter((r) => r.status?.toLowerCase() === 'in_progress').length;
    const resolved = supportRequests.filter((r) => r.status?.toLowerCase() === 'resolved').length;
    const closed = supportRequests.filter((r) => r.status?.toLowerCase() === 'closed').length;
    return { open, inProgress, resolved, closed, total: supportRequests.length };
  }, [supportRequests]);

  // Filter tickets
  const filteredTickets = useMemo(() => {
    return supportRequests.filter((ticket) => {
      // Status filter
      if (statusFilter !== 'all' && ticket.status?.toLowerCase() !== statusFilter) {
        return false;
      }
      // Priority filter
      if (priorityFilter !== 'all' && ticket.priority?.toLowerCase() !== priorityFilter) {
        return false;
      }
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const subject = (ticket.subject || '').toLowerCase();
        const description = (ticket.description || '').toLowerCase();
        const requesterName = (ticket.requester_name || '').toLowerCase();
        if (!subject.includes(query) && !description.includes(query) && !requesterName.includes(query)) {
          return false;
        }
      }
      return true;
    });
  }, [supportRequests, statusFilter, priorityFilter, searchQuery]);

  const getStatusColor = (status?: string): string => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'resolved':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'closed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getPriorityColor = (priority?: string): string => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getTypeLabel = (type?: string): string => {
    const labels: Record<string, string> = {
      technical: 'Technical',
      billing: 'Billing',
      general: 'General',
      feature_request: 'Feature Request',
      bug_report: 'Bug Report',
    };
    return (type && labels[type]) || type || 'General';
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatRelativeTime = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  // Show empty state for AWS users when no client is selected
  if (isAWSUser && !selectedClientId) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Support</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">View and manage support tickets</p>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-12 text-center">
            <TicketIcon className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Please select a client to view support tickets
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Use the client selector in the navigation bar to choose a client.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const hasActiveFilters = !!(searchQuery || statusFilter !== 'all' || priorityFilter !== 'all');
  let emptyTicketsMessage: string;
  if (hasActiveFilters) emptyTicketsMessage = 'Try adjusting your filters or search query';
  else if (canCreateTicket) emptyTicketsMessage = "You haven't submitted any support tickets yet";
  else emptyTicketsMessage = 'No support tickets from your clients yet';

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Support</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {isClientUser
              ? 'View your support tickets and submit new requests'
              : 'Manage support tickets for your clients'}
          </p>
        </div>
        {canCreateTicket && (
          <button
            onClick={() => setShowSupportRequest(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
          >
            <Plus className="w-4 h-4" />
            New Ticket
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Open</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.open}</p>
            </div>
            <FiAlertCircle className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">In Progress</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.inProgress}</p>
            </div>
            <FiClock className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Resolved</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.resolved}</p>
            </div>
            <FiCheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-gray-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Closed</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.closed}</p>
            </div>
            <FiXCircle className="w-8 h-8 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Status Tabs */}
            <div className="flex gap-1 overflow-x-auto">
              {STATUS_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isActive = statusFilter === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => setStatusFilter(option.value)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {option.label}
                    {option.value === 'open' && stats.open > 0 && (
                      <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${isActive ? 'bg-blue-500' : 'bg-yellow-500 text-white'}`}>
                        {stats.open}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex-1" />

            {/* Priority Filter */}
            <div className="flex items-center gap-2">
              <FiFilter className="w-4 h-4 text-gray-400" />
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-gray-700 dark:text-gray-300"
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search tickets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 w-full lg:w-64"
              />
            </div>
          </div>
        </div>

        {/* Ticket List */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        )}
        {!loading && filteredTickets.length === 0 && (
          <div className="px-6 py-12 text-center">
            <TicketIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No tickets found</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {emptyTicketsMessage}
            </p>
            {canCreateTicket && !searchQuery && statusFilter === 'all' && priorityFilter === 'all' && (
              <button
                onClick={() => setShowSupportRequest(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Create your first ticket
              </button>
            )}
          </div>
        )}
        {!loading && filteredTickets.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ticket
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  {canManageSupport && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Requester
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Replies
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTickets.map((ticket) => {
                  let assigneeLabel: string;
                  if (ticket.assignee === 'aivilo') assigneeLabel = 'Aivilo Support';
                  else if (ticket.assignee === 'aws') assigneeLabel = 'AWS Rep';
                  else assigneeLabel = ticket.assignee || '—';
                  return (
                  <tr
                    key={ticket.id}
                    onClick={() => handleSelectTicket(ticket)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelectTicket(ticket); }} role="row" tabIndex={0}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="max-w-xs">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {ticket.subject || 'No subject'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {ticket.description?.substring(0, 60) || 'No description'}
                          {(ticket.description?.length ?? 0) > 60 ? '...' : ''}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {getTypeLabel(ticket.request_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority
                          ? ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)
                          : 'Medium'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(ticket.status)}`}>
                        {ticket.status
                          ? ticket.status.replace('_', ' ').charAt(0).toUpperCase() + ticket.status.replace('_', ' ').slice(1)
                          : 'Open'}
                      </span>
                    </td>
                    {canManageSupport && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <FiUser className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {ticket.requester_name || ticket.requester_email || '—'}
                          </span>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {assigneeLabel}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <FiCalendar className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {formatRelativeTime(ticket.created_at)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <FiMessageSquare className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {(ticket.replies as unknown[])?.length || 0}
                        </span>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Results count */}
        {!loading && filteredTickets.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
            Showing {filteredTickets.length} of {stats.total} tickets
          </div>
        )}
      </div>

      {/* Modals */}
      {showSupportRequest && (
        <SupportRequest
          onClose={() => setShowSupportRequest(false)}
        />
      )}

      {selectedTicket && (
        <TicketDetail
          onClose={async () => {
            await loadSupportRequests();
            handleSelectTicket(null);
          }}
          ticket={selectedTicket as Ticket}
        />
      )}
    </div>
  );
};

export default Support;
